import { type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { sort } from 'ramda';
import { useSelector } from 'react-redux';

import { EOrganizedBy } from 'detailsv2/structure';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';

import { useCollapseDays } from '../functions';
import { IDay, IDayProps } from '../types';

export const useDay = ({ day }: IDayProps) => {
  const proposal = useSelector(itinerariesItemDataSelector);
  const { isCollapsed } = useCollapseDays();
  const showDates = proposal.scheduleType === EOrganizedBy.Date;

  const dayRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const highlighted = Boolean(
    proposal.highlightedDays?.includes(day.date) || proposal.highlightedDays?.includes(day.day.toString()),
  );

  const [showDay, setShowDay] = useState<boolean>(false);
  const onToggle = () => {
    setShowDay(!showDay);
  };

  const shouldScrollTo = useMemo(() => {
    if (!proposal.highlightedDays) return false;

    const sortedDays = sort((a: string, b: string) => (a < b ? -1 : 1), proposal.highlightedDays);

    const check = (day: string) => sortedDays.indexOf(day) === 0;

    return check(day.date || '') || check(day.day.toString());
  }, [proposal.highlightedDays]);

  useEffect(() => {
    if (!isCollapsed) {
      setShowDay(false);
    }
  }, [isCollapsed]);

  useEffect(() => {
    if (highlighted && shouldScrollTo) {
      dayRef.current?.offsetParent?.scrollTo({
        top: dayRef.current?.offsetTop || 0,
        behavior: 'smooth',
      });
    }
  }, [highlighted, shouldScrollTo]);

  return {
    dayRef,
    showDates,
    showDay,
    onToggle,
    highlighted,
  };
};

export const useDayHeight = ({ day, isCollapsed }: { day: IDay; isCollapsed: boolean }) => {
  const dayHeightShift = 19;
  const [dayHeight, setDayHeight] = useState(0);
  const calcDayHeight = useCallback(
    (dayNode: HTMLDivElement) => {
      if (dayNode) {
        setDayHeight(dayNode.getBoundingClientRect()?.height || dayHeightShift);
      }
    },
    [day, isCollapsed],
  ); // do not remove "day" dependency, in order to recalculate lines heights

  return { dayHeight: dayHeight - dayHeightShift, calcDayHeight };
};
